/**
 * Identity Page
 * @description Tenant 관리자 - 인증 설정 페이지
 */

import React, { useState, useEffect } from 'react';
import './tenant-admin.css';
import {
  Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent,
  SelectItem, SelectTrigger, SelectValue, Switch, Tabs, TabsContent, TabsList,
  TabsTrigger, Textarea
} from '@shared/ui/primitives';
import { getTenantIdentity, getTenantIdentityStats, updateTenantIdentity, getTenantSessions, revokeTenantSession, revokeAllTenantSessions, IdentityConfig, IdentityProvider, AuthenticationConfig, TenantSession } from '@/services/tenantConfigApi';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import {
    Shield,
    Key,
    Users,
    Settings,
    CheckCircle,
    AlertTriangle,
    Lock,
    Smartphone,
    Plus,
    Edit,
    Power,
    PowerOff,
    Loader2
} from 'lucide-react';

export const IdentityPage: React.FC = () => {
    const { user } = useAuthStore();
    const tenantId = user?.userType === 'tenant-user' ? user.tenantId : '';

    const [identityConfig, setIdentityConfig] = useState<IdentityConfig | null>(null);
    const [orgUserCount, setOrgUserCount] = useState<number>(0);
    const [identityStats, setIdentityStats] = useState<{ totalUsers: number; mfaEnabledUsers: number } | null>(null);
    const [sessions, setSessions] = useState<TenantSession[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
    const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [editingProvider, setEditingProvider] = useState<IdentityProvider | null>(null);
    const [editingPolicy, setEditingPolicy] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('providers');

    // 데이터 로드
    useEffect(() => {
        if (!tenantId) {
            setLoading(false);
            return;
        }
        loadIdentityConfig();
    }, [tenantId]);

    const loadIdentityConfig = async () => {
        if (!tenantId) return;
        try {
            setLoading(true);
            const LOAD_TIMEOUT_MS = 15000;
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('요청 시간이 초과되었습니다.')), LOAD_TIMEOUT_MS)
            );
            const dataPromise = Promise.all([
                getTenantIdentity(tenantId),
                dataService.getTenantOrganization(tenantId).catch(() => ({ users: [] })),
                getTenantIdentityStats(tenantId).catch(() => ({ totalUsers: 0, mfaEnabledUsers: 0 })),
            ]);
            const [config, orgData, stats] = await Promise.race([dataPromise, timeoutPromise]);
            setIdentityConfig(config);
            setOrgUserCount(Array.isArray(orgData?.users) ? orgData.users.length : 0);
            setIdentityStats(stats);
            setLoading(false);
            setSessionsLoading(true);
            const sessionTimeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('timeout')), 10000)
            );
            Promise.race([
                getTenantSessions(tenantId).then((res) => res?.sessions ?? []),
                sessionTimeoutPromise,
            ])
                .then((list) => setSessions(Array.isArray(list) ? list : []))
                .catch(() => setSessions([]))
                .finally(() => setSessionsLoading(false));
        } catch (error) {
            console.error('Failed to load identity config:', error);
            setLoading(false);
            alert((error as Error)?.message ?? '인증 설정을 불러오는데 실패했습니다.');
        }
    };

    const saveIdentityConfig = async (updates: Partial<IdentityConfig>) => {
        if (!tenantId || !identityConfig) return;
        try {
            setSaving(true);
            const updated = await updateTenantIdentity(tenantId, {
                ...identityConfig,
                ...updates,
            });
            setIdentityConfig(updated);
            alert('설정이 저장되었습니다.');
        } catch (error) {
            console.error('Failed to save identity config:', error);
            alert('설정 저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // 제공자 목록 (identityConfig에서 변환, Local은 조직(org) 사용자 수로 통계 표시)
    const authProviders = identityConfig?.identityProviders?.map(idp => ({
        id: idp.idpId,
        name: idp.displayName,
        type: idp.type === 'local' ? 'Local Database' : idp.type === 'oidc' ? 'OAuth Provider' : 'Single Sign-On',
        status: idp.enabled ? 'active' : 'inactive',
        users: idp.type === 'local' ? orgUserCount : 0,
        description: idp.type === 'local' ? '내부 데이터베이스 기반 사용자 인증' :
                     idp.type === 'oidc' ? 'OAuth 2.0 기반 인증' : 'SAML 기반 Single Sign-On',
        provider: idp
    })) || [];

    // 보안 정책 목록 (identityConfig에서 변환)
    const securityPolicies = [
        {
            id: 'password',
            name: '비밀번호 정책',
            description: identityConfig?.authentication?.passwordPolicy 
                ? `최소 ${identityConfig.authentication.passwordPolicy.minLength || 8}자, ${[
                    identityConfig.authentication.passwordPolicy.requireUppercase ? '대문자' : '',
                    identityConfig.authentication.passwordPolicy.requireLowercase ? '소문자' : '',
                    identityConfig.authentication.passwordPolicy.requireNumbers ? '숫자' : '',
                    identityConfig.authentication.passwordPolicy.requireSpecialChars ? '특수문자' : ''
                ].filter(Boolean).join(', ')} 포함`
                : '비밀번호 정책 설정',
            status: identityConfig?.authentication?.passwordPolicy ? 'enabled' : 'disabled',
            policy: identityConfig?.authentication?.passwordPolicy
        },
        {
            id: 'session',
            name: '세션 타임아웃',
            description: identityConfig?.authentication?.session
                ? `타임아웃: ${(identityConfig.authentication.session.timeoutSeconds || 3600) / 60}분, 최대 동시 세션: ${identityConfig.authentication.session.maxConcurrentSessions || 3}개`
                : '세션 타임아웃 설정',
            status: identityConfig?.authentication?.session ? 'enabled' : 'disabled',
            policy: identityConfig?.authentication?.session
        },
        {
            id: 'mfa',
            name: '2단계 인증',
            description: identityConfig?.authentication?.methods?.find(m => m.type === 'mfa-otp' && m.enabled)
                ? 'TOTP 기반 다중 인증 활성화'
                : '2단계 인증 비활성화',
            status: identityConfig?.authentication?.methods?.find(m => m.type === 'mfa-otp' && m.enabled) ? 'enabled' : 'disabled',
            policy: identityConfig?.authentication?.methods?.find(m => m.type === 'mfa-otp')
        }
    ];

    // 핸들러 함수들
    const handleSettingsManage = () => {
        setIsSettingsModalOpen(true);
    };

    const handleAddProvider = () => {
        setEditingProvider(null);
        setIsProviderModalOpen(true);
    };

    const handleProviderSettings = (provider: any) => {
        setEditingProvider(provider.provider);
        setIsProviderModalOpen(true);
    };

    const handleToggleProvider = async (providerId: string) => {
        if (!identityConfig) return;
        const updatedProviders = identityConfig.identityProviders?.map(idp =>
            idp.idpId === providerId ? { ...idp, enabled: !idp.enabled } : idp
        ) || [];
        await saveIdentityConfig({ identityProviders: updatedProviders });
    };

    const handleEditPolicy = (policyId: string) => {
        setEditingPolicy(policyId);
        setIsPolicyModalOpen(true);
    };

    const handleSaveProvider = async (provider: IdentityProvider) => {
        if (!identityConfig) return;
        const existingProviders = identityConfig.identityProviders || [];
        const existingIndex = existingProviders.findIndex(p => p.idpId === provider.idpId);
        
        let updatedProviders: IdentityProvider[];
        if (existingIndex >= 0) {
            // 기존 제공자 업데이트
            updatedProviders = existingProviders.map((p, idx) => 
                idx === existingIndex ? provider : p
            );
        } else {
            // 새 제공자 추가
            updatedProviders = [...existingProviders, provider];
        }

        await saveIdentityConfig({ identityProviders: updatedProviders });
        setIsProviderModalOpen(false);
        setEditingProvider(null);
    };

    const handleSavePolicy = async (policyId: string, policyData: any) => {
        if (!identityConfig) return;
        const auth = identityConfig.authentication || {};

        if (policyId === 'password') {
            await saveIdentityConfig({
                authentication: {
                    ...auth,
                    passwordPolicy: policyData
                }
            });
        } else if (policyId === 'session') {
            await saveIdentityConfig({
                authentication: {
                    ...auth,
                    session: policyData
                }
            });
        } else if (policyId === 'mfa') {
            const methods = auth.methods || [];
            const mfaIndex = methods.findIndex(m => m.type === 'mfa-otp');
            
            if (mfaIndex >= 0) {
                // 기존 MFA 메서드 업데이트
                methods[mfaIndex] = { ...methods[mfaIndex], ...policyData };
            } else {
                // 새 MFA 메서드 추가
                methods.push({
                    type: 'mfa-otp',
                    enabled: policyData.enabled ?? false,
                    provider: policyData.provider || 'totp',
                    issuer: policyData.issuer || 'ITOPS'
                });
            }

            await saveIdentityConfig({
                authentication: {
                    ...auth,
                    methods
                }
            });
        }

        setIsPolicyModalOpen(false);
        setEditingPolicy(null);
    };

    const SESSION_LOAD_TIMEOUT_MS = 10000;

    const loadSessions = async () => {
        if (!tenantId) return;
        setSessionsLoading(true);
        try {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('세션 목록 요청 시간 초과')), SESSION_LOAD_TIMEOUT_MS)
            );
            const dataPromise = getTenantSessions(tenantId).then((res) => res?.sessions ?? []);
            const list = await Promise.race([dataPromise, timeoutPromise]);
            setSessions(Array.isArray(list) ? list : []);
        } catch (e) {
            setSessions([]);
        } finally {
            setSessionsLoading(false);
        }
    };

    const handleTerminateSession = async (sessionId: string) => {
        if (!tenantId) return;
        try {
            await revokeTenantSession(tenantId, sessionId);
            await loadSessions();
        } catch (e) {
            alert((e as Error)?.message ?? '세션 종료에 실패했습니다.');
        }
    };

    const handleTerminateAllSessions = async () => {
        if (!tenantId) return;
        if (!confirm('모든 활성 세션을 종료합니다. 현재 로그인된 모든 사용자가 다시 로그인해야 합니다. 계속하시겠습니까?')) return;
        try {
            await revokeAllTenantSessions(tenantId);
            useAuthStore.getState().logout();
            window.location.href = '/tenant-admin/login';
        } catch (e: unknown) {
            const msg = (e as Error)?.message ?? '';
            if (msg.includes('401') || msg.includes('Session expired') || msg.includes('invalid')) {
                useAuthStore.getState().logout();
                window.location.href = '/tenant-admin/login';
                return;
            }
            alert(msg || '전체 세션 종료에 실패했습니다.');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
            case 'enabled':
                return <Badge className="bg-green-100 text-green-800">활성</Badge>;
            case 'inactive':
            case 'disabled':
                return <Badge variant="secondary">비활성</Badge>;
            default:
                return <Badge variant="secondary">알 수 없음</Badge>;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!user || user.userType !== 'tenant-user' || !tenantId) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground">테넌트 사용자로 로그인해 주세요.</p>
            </div>
        );
    }

    return (
        <div className="tenant-admin-page space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-foreground">인증 설정</h1>
                    <p className="text-muted-foreground mt-1">
                        사용자 인증 및 보안 정책을 관리합니다.
                    </p>
                </div>
                <Button 
                    className="flex items-center gap-2" 
                    onClick={handleSettingsManage}
                    disabled={saving}
                >
                    <Settings className="w-4 h-4" />
                    설정 관리
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">총 사용자</p>
                            <p className="text-2xl font-bold text-foreground">
                                {identityStats?.totalUsers ?? authProviders.reduce((sum, p) => sum + p.users, 0)}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">활성 인증</p>
                            <p className="text-2xl font-bold text-foreground">
                                {authProviders.filter(p => p.status === 'active').length}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <Lock className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">보안 정책</p>
                            <p className="text-2xl font-bold text-foreground">
                                {securityPolicies.filter(p => p.status === 'enabled').length}
                            </p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Smartphone className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">2FA 사용자</p>
                            <p className="text-2xl font-bold text-foreground">
                                {identityStats?.mfaEnabledUsers ?? 0}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Main Content */}
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'sessions') loadSessions(); }} className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="providers" className="flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        인증 제공자
                    </TabsTrigger>
                    <TabsTrigger value="policies" className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        보안 정책
                    </TabsTrigger>
                    <TabsTrigger value="sessions" className="flex items-center gap-2" disabled>
                        <Users className="w-4 h-4" />
                        세션 관리
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="providers" className="space-y-4">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">인증 제공자</h2>
                            <Button variant="outline" onClick={handleAddProvider} className="flex items-center gap-2">
                                <Plus className="w-4 h-4" />
                                새 제공자 추가
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {authProviders.map((provider) => (
                                <div
                                    key={provider.id}
                                    className="flex items-center justify-between p-4 border rounded-lg"
                                >
                                    <div className="flex items-center gap-4">
                                        <Key className="w-8 h-8 text-muted-foreground" />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-medium">{provider.name}</h3>
                                                {getStatusBadge(provider.status)}
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {provider.description}
                                            </p>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                <span>유형: {provider.type}</span>
                                                <span>사용자: {provider.users}명</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => handleProviderSettings(provider)}
                                            disabled={saving}
                                        >
                                            설정
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="policies" className="space-y-4">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">보안 정책</h2>
                        </div>

                        <div className="space-y-4">
                            {securityPolicies.map((policy, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-4 border rounded-lg"
                                >
                                    <div className="flex items-center gap-4">
                                        {policy.status === 'enabled' ? (
                                            <CheckCircle className="w-6 h-6 text-green-600" />
                                        ) : (
                                            <AlertTriangle className="w-6 h-6 text-gray-400" />
                                        )}
                                        <div>
                                            <h3 className="font-medium">{policy.name}</h3>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {policy.description}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {getStatusBadge(policy.status)}
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => handleEditPolicy(policy.id)}
                                            disabled={saving}
                                        >
                                            편집
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="sessions" className="space-y-4">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold">활성 세션</h2>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={loadSessions}
                                    disabled={sessionsLoading}
                                >
                                    {sessionsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '새로고침'}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleTerminateAllSessions}
                                >
                                    모든 세션 종료
                                </Button>
                            </div>
                        </div>

                        {sessionsLoading && sessions.length === 0 ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : sessions.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>활성 세션이 없습니다.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-3 px-2 font-medium">사용자 ID</th>
                                            <th className="text-left py-3 px-2 font-medium">생성 시각</th>
                                            <th className="text-left py-3 px-2 font-medium">마지막 활동</th>
                                            <th className="text-left py-3 px-2 font-medium">User-Agent</th>
                                            <th className="w-[100px] py-3 px-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.map((s) => (
                                            <tr key={s.sessionId} className="border-b last:border-0">
                                                <td className="py-3 px-2">{s.userId}</td>
                                                <td className="py-3 px-2 text-muted-foreground">
                                                    {s.createdAt ? new Date(s.createdAt).toLocaleString('ko-KR') : '-'}
                                                </td>
                                                <td className="py-3 px-2 text-muted-foreground">
                                                    {s.lastActivityAt ? new Date(s.lastActivityAt).toLocaleString('ko-KR') : '-'}
                                                </td>
                                                <td className="py-3 px-2 text-muted-foreground max-w-[200px] truncate" title={s.userAgent ?? ''}>
                                                    {s.userAgent || '-'}
                                                </td>
                                                <td className="py-3 px-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleTerminateSession(s.sessionId)}
                                                    >
                                                        세션 종료
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>

            {/* 제공자 추가/편집 모달 */}
            <Dialog open={isProviderModalOpen} onOpenChange={setIsProviderModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProvider ? '인증 제공자 편집' : '새 인증 제공자 추가'}
                        </DialogTitle>
                        <DialogDescription>
                            인증 제공자를 추가하거나 편집합니다.
                        </DialogDescription>
                    </DialogHeader>
                    <ProviderForm
                        provider={editingProvider}
                        totalProviderCount={identityConfig?.identityProviders?.length ?? 0}
                        onSave={handleSaveProvider}
                        onCancel={() => {
                            setIsProviderModalOpen(false);
                            setEditingProvider(null);
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* 정책 편집 모달 */}
            <Dialog open={isPolicyModalOpen} onOpenChange={setIsPolicyModalOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>보안 정책 편집</DialogTitle>
                        <DialogDescription>
                            {editingPolicy === 'password' && '비밀번호 정책을 설정합니다.'}
                            {editingPolicy === 'session' && '세션 타임아웃 정책을 설정합니다.'}
                            {editingPolicy === 'mfa' && '2단계 인증(MFA) 정책을 설정합니다.'}
                        </DialogDescription>
                    </DialogHeader>
                    <PolicyForm
                        policyId={editingPolicy || ''}
                        policy={editingPolicy === 'password' ? identityConfig?.authentication?.passwordPolicy : 
                                editingPolicy === 'session' ? identityConfig?.authentication?.session : 
                                editingPolicy === 'mfa' ? identityConfig?.authentication?.methods?.find(m => m.type === 'mfa-otp') : null}
                        onSave={(data) => editingPolicy && handleSavePolicy(editingPolicy, data)}
                        onCancel={() => {
                            setIsPolicyModalOpen(false);
                            setEditingPolicy(null);
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* 설정 관리 모달 */}
            <Dialog open={isSettingsModalOpen} onOpenChange={setIsSettingsModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>인증 설정 관리</DialogTitle>
                        <DialogDescription>
                            전체 인증 설정을 관리합니다.
                        </DialogDescription>
                    </DialogHeader>
                    <SettingsForm
                        config={identityConfig}
                        onSave={saveIdentityConfig}
                        onCancel={() => setIsSettingsModalOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

// 제공자 폼 컴포넌트
const ProviderForm: React.FC<{
    provider: IdentityProvider | null;
    totalProviderCount?: number;
    onSave: (provider: IdentityProvider) => void;
    onCancel: () => void;
}> = ({ provider, totalProviderCount = 0, onSave, onCancel }) => {
    const isOnlyProvider = totalProviderCount <= 1;
    const [formData, setFormData] = useState<Partial<IdentityProvider>>({
        idpId: provider?.idpId || '',
        type: provider?.type || 'oidc',
        displayName: provider?.displayName || '',
        enabled: isOnlyProvider ? true : (provider?.enabled ?? true),
        oidc: provider?.oidc || { issuer: '', clientId: '', scopes: ['openid', 'profile', 'email'] },
        saml: provider?.saml || { idpMetadataUrl: '', spEntityId: '' },
        mapping: provider?.mapping || {}
    });

    useEffect(() => {
        const enabled = isOnlyProvider ? true : (provider?.enabled ?? true);
        setFormData({
            idpId: provider?.idpId || '',
            type: provider?.type || 'oidc',
            displayName: provider?.displayName || '',
            enabled,
            oidc: provider?.oidc || { issuer: '', clientId: '', scopes: ['openid', 'profile', 'email'] },
            saml: provider?.saml || { idpMetadataUrl: '', spEntityId: '' },
            mapping: provider?.mapping || {}
        });
    }, [provider, isOnlyProvider]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.idpId || !formData.displayName) {
            alert('ID와 표시명은 필수입니다.');
            return;
        }
        const toSave = { ...formData, enabled: isOnlyProvider ? true : (formData.enabled ?? true) } as IdentityProvider;
        onSave(toSave);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="idpId">제공자 ID *</Label>
                <Input
                    id="idpId"
                    value={formData.idpId}
                    onChange={(e) => setFormData({ ...formData, idpId: e.target.value })}
                    placeholder="예: azure-ad"
                    required
                    disabled={!!provider}
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="displayName">표시명 *</Label>
                <Input
                    id="displayName"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="예: Azure AD"
                    required
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="type">유형</Label>
                <Select
                    value={formData.type}
                    onValueChange={(value: 'local' | 'oidc' | 'saml') => setFormData({ ...formData, type: value })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="local">로컬</SelectItem>
                        <SelectItem value="oidc">OIDC/OAuth 2.0</SelectItem>
                        <SelectItem value="saml">SAML</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {formData.type === 'oidc' && (
                <>
                    <div className="space-y-2">
                        <Label htmlFor="issuer">Issuer URL *</Label>
                        <Input
                            id="issuer"
                            value={formData.oidc?.issuer || ''}
                            onChange={(e) => setFormData({
                                ...formData,
                                oidc: { ...formData.oidc, issuer: e.target.value } as any
                            })}
                            placeholder="https://login.microsoftonline.com/{tenantId}/v2.0"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="clientId">Client ID *</Label>
                        <Input
                            id="clientId"
                            value={formData.oidc?.clientId || ''}
                            onChange={(e) => setFormData({
                                ...formData,
                                oidc: { ...formData.oidc, clientId: e.target.value } as any
                            })}
                            placeholder="Client ID"
                            required
                        />
                    </div>
                </>
            )}

            {formData.type === 'saml' && (
                <>
                    <div className="space-y-2">
                        <Label htmlFor="idpMetadataUrl">IdP Metadata URL *</Label>
                        <Input
                            id="idpMetadataUrl"
                            value={formData.saml?.idpMetadataUrl || ''}
                            onChange={(e) => setFormData({
                                ...formData,
                                saml: { ...formData.saml, idpMetadataUrl: e.target.value } as any
                            })}
                            placeholder="https://idp.example.com/metadata"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="spEntityId">SP Entity ID *</Label>
                        <Input
                            id="spEntityId"
                            value={formData.saml?.spEntityId || ''}
                            onChange={(e) => setFormData({
                                ...formData,
                                saml: { ...formData.saml, spEntityId: e.target.value } as any
                            })}
                            placeholder="urn:example:sp"
                            required
                        />
                    </div>
                </>
            )}

            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                        <Label htmlFor="enabled" className="text-sm font-medium">활성화</Label>
                        <p className="text-xs text-muted-foreground">
                            {isOnlyProvider
                                ? '인증 제공자가 1개일 때는 항상 활성화됩니다.'
                                : (formData.enabled ? '이 제공자가 로그인에 사용됩니다.' : '비활성화 시 이 제공자로는 로그인할 수 없습니다.')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Switch
                            id="enabled"
                            checked={isOnlyProvider ? true : (formData.enabled ?? true)}
                            onCheckedChange={(checked) => !isOnlyProvider && setFormData({ ...formData, enabled: checked })}
                            disabled={isOnlyProvider}
                        />
                        <span className={`text-sm font-medium min-w-[4rem] ${(isOnlyProvider ? true : formData.enabled) ? 'text-green-600' : 'text-muted-foreground'}`}>
                            {(isOnlyProvider ? true : formData.enabled) ? '활성화' : '비활성화'}
                        </span>
                    </div>
                </div>
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" onClick={onCancel}>
                    취소
                </Button>
                <Button type="submit">저장</Button>
            </DialogFooter>
        </form>
    );
};

// 정책 폼 컴포넌트
const PolicyForm: React.FC<{
    policyId: string;
    policy: any;
    onSave: (data: any) => void;
    onCancel: () => void;
}> = ({ policyId, policy, onSave, onCancel }) => {
    if (policyId === 'password') {
        const [formData, setFormData] = useState({
            minLength: policy?.minLength || 8,
            requireUppercase: policy?.requireUppercase ?? true,
            requireLowercase: policy?.requireLowercase ?? true,
            requireNumbers: policy?.requireNumbers ?? true,
            requireSpecialChars: policy?.requireSpecialChars ?? true,
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSave(formData);
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="minLength">최소 길이</Label>
                    <Input
                        id="minLength"
                        type="number"
                        min="4"
                        max="128"
                        value={formData.minLength}
                        onChange={(e) => setFormData({ ...formData, minLength: parseInt(e.target.value) || 8 })}
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="requireUppercase"
                            checked={formData.requireUppercase}
                            onCheckedChange={(checked) => setFormData({ ...formData, requireUppercase: checked })}
                        />
                        <Label htmlFor="requireUppercase">대문자 필수</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="requireLowercase"
                            checked={formData.requireLowercase}
                            onCheckedChange={(checked) => setFormData({ ...formData, requireLowercase: checked })}
                        />
                        <Label htmlFor="requireLowercase">소문자 필수</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="requireNumbers"
                            checked={formData.requireNumbers}
                            onCheckedChange={(checked) => setFormData({ ...formData, requireNumbers: checked })}
                        />
                        <Label htmlFor="requireNumbers">숫자 필수</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="requireSpecialChars"
                            checked={formData.requireSpecialChars}
                            onCheckedChange={(checked) => setFormData({ ...formData, requireSpecialChars: checked })}
                        />
                        <Label htmlFor="requireSpecialChars">특수문자 필수</Label>
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        취소
                    </Button>
                    <Button type="submit">저장</Button>
                </DialogFooter>
            </form>
        );
    }

    if (policyId === 'session') {
        const [formData, setFormData] = useState({
            timeoutSeconds: policy?.timeoutSeconds || 3600,
            maxConcurrentSessions: policy?.maxConcurrentSessions || 3,
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSave(formData);
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="timeoutSeconds">세션 타임아웃 (초)</Label>
                    <Input
                        id="timeoutSeconds"
                        type="number"
                        min="60"
                        max="86400"
                        value={formData.timeoutSeconds}
                        onChange={(e) => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) || 3600 })}
                    />
                    <p className="text-sm text-muted-foreground">
                        {formData.timeoutSeconds / 60}분 ({formData.timeoutSeconds}초)
                    </p>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="maxConcurrentSessions">최대 동시 세션 수</Label>
                    <Input
                        id="maxConcurrentSessions"
                        type="number"
                        min="1"
                        max="10"
                        value={formData.maxConcurrentSessions}
                        onChange={(e) => setFormData({ ...formData, maxConcurrentSessions: parseInt(e.target.value) || 3 })}
                    />
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        취소
                    </Button>
                    <Button type="submit">저장</Button>
                </DialogFooter>
            </form>
        );
    }

    if (policyId === 'mfa') {
        const mfaMethod = policy || { enabled: false, provider: 'totp', issuer: 'ITOPS' };
        const [formData, setFormData] = useState({
            enabled: mfaMethod.enabled ?? false,
            provider: mfaMethod.provider || 'totp',
            issuer: mfaMethod.issuer || 'ITOPS',
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            onSave(formData);
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3 rounded-xl border-2 border-slate-200 bg-slate-50 p-5 shadow-sm">
                    <div className="flex items-center gap-4">
                        <Switch
                            id="mfaEnabled"
                            checked={formData.enabled}
                            onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                            className="shrink-0 data-[state=unchecked]:bg-slate-300 data-[state=unchecked]:border-2 data-[state=unchecked]:border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-2 data-[state=checked]:border-blue-700"
                        />
                        <div className="flex-1">
                            <Label htmlFor="mfaEnabled" className="text-base font-semibold cursor-pointer text-slate-800">
                                2단계 인증 활성화
                            </Label>
                            <p className="text-sm text-slate-600 mt-0.5">
                                2단계 인증을 활성화하면 사용자가 로그인 시 추가 인증 단계를 거치게 됩니다.
                            </p>
                        </div>
                    </div>
                </div>

                {formData.enabled && (
                    <>
                        <div className="space-y-2">
                            <Label htmlFor="mfaProvider">인증 제공자</Label>
                            <Select
                                value={formData.provider}
                                onValueChange={(value) => setFormData({ ...formData, provider: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="totp">TOTP (Time-based One-Time Password)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="mfaIssuer">발행자 이름</Label>
                            <Input
                                id="mfaIssuer"
                                value={formData.issuer}
                                onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                                placeholder="예: ITOPS"
                            />
                            <p className="text-sm text-muted-foreground">
                                인증 앱에 표시될 발행자 이름입니다.
                            </p>
                        </div>
                    </>
                )}

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        취소
                    </Button>
                    <Button type="submit">저장</Button>
                </DialogFooter>
            </form>
        );
    }

    return null;
};

// 설정 관리 폼 컴포넌트
const SettingsForm: React.FC<{
    config: IdentityConfig | null;
    onSave: (updates: Partial<IdentityConfig>) => Promise<void>;
    onCancel: () => void;
}> = ({ config, onSave, onCancel }) => {
    if (!config) return null;

    return (
        <div className="space-y-4">
            <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-2">Audit 설정</h3>
                <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="log_access"
                            checked={config.audit?.log_access ?? false}
                            onCheckedChange={(checked) => {
                                onSave({
                                    audit: { ...config.audit, log_access: checked }
                                });
                            }}
                        />
                        <Label htmlFor="log_access">접근 로그 기록</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Switch
                            id="log_changes"
                            checked={config.audit?.log_changes ?? false}
                            onCheckedChange={(checked) => {
                                onSave({
                                    audit: { ...config.audit, log_changes: checked }
                                });
                            }}
                        />
                        <Label htmlFor="log_changes">변경 로그 기록</Label>
                    </div>
                </div>
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onCancel}>
                    닫기
                </Button>
            </DialogFooter>
        </div>
    );
};

IdentityPage.displayName = 'IdentityPage';